import { moneyLib } from "@agentcall/core/lib"
import { Plus, Trash } from "react-feather"
import type { UseFormReturn } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { PriceWithTax } from "@/components/ui/price-with-tax"
import { cn } from "@/lib/utils"

// フォームラベルコンポーネント
export function FormFieldLabel({
  label,
  required = false,
  disabled = false,
}: {
  label: string
  required?: boolean
  disabled?: boolean
}) {
  return (
    <div className="flex items-center gap-[5px]">
      <span
        className={cn(
          "text-sm font-semibold leading-6",
          disabled ? "text-[#9eafc7]" : "text-[#3f4652]"
        )}
      >
        {label}
      </span>
      {required && (
        <span className="bg-[#ff3232] text-white text-xs font-semibold px-[5px] py-1 rounded-[3px]">
          必須
        </span>
      )}
    </div>
  )
}

// 数字のみ入力を許可するハンドラー
export const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
  // 許可するキー: 数字、Backspace、Delete、Tab、Enter、矢印キー、Home、End
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ]

  if (allowedKeys.includes(e.key)) {
    return
  }

  // Ctrl/Cmd + A, C, V, X を許可
  if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) {
    return
  }

  // 数字以外は入力を防止
  if (!/^\d$/.test(e.key)) {
    e.preventDefault()
  }
}

// 数字のみにフィルタリングするペーストハンドラー
export const handleNumericPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
  const pastedText = e.clipboardData.getData("text")
  if (!/^\d*$/.test(pastedText)) {
    e.preventDefault()
    // 数字のみを抽出して貼り付け
    const numericOnly = pastedText.replace(/\D/g, "")
    if (numericOnly) {
      document.execCommand("insertText", false, numericOnly)
    }
  }
}

// 金額合計を計算
export const calculateTotal = (baseFee?: string, tax?: string): number => {
  const baseAmount = Number.parseFloat(baseFee || "0")
  const taxAmount = Number.parseFloat(tax || "0")
  return baseAmount + taxAmount
}

// その他費用の型定義
export interface OtherFee {
  name: string
  baseFee: string
  tax: string
}

export interface Occupant {
  name: string
  nameKana: string
  phone: string
  birthday: string
  note: string
}

// フォームの入居者入力を API のリクエスト形式へ変換する。
// 未入力の任意項目は null に寄せる。
export const toOccupantsPayload = (occupants: Occupant[]) =>
  occupants.map((occupant) => ({
    name: occupant.name,
    nameKana: occupant.nameKana,
    phone: occupant.phone === "" ? null : occupant.phone,
    birthday: occupant.birthday === "" ? null : occupant.birthday,
    note: occupant.note === "" ? null : occupant.note,
    gender: null,
  }))

// API のレスポンスをフォームの入居者入力へ変換する。
export const toOccupantsFormValues = (
  occupants:
    | Array<{
        name: string
        nameKana: string
        phone?: string | null
        birthday: string | null
        note?: string | null
      }>
    | undefined
): Occupant[] =>
  (occupants ?? []).map((occupant) => ({
    name: occupant.name,
    nameKana: occupant.nameKana,
    phone: occupant.phone ?? "",
    birthday: occupant.birthday ?? "",
    note: occupant.note ?? "",
  }))

// 駐車場フィールドコンポーネント
export function ParkingFields({
  form,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: react-hook-formの型が複雑なためanyを使用
  form: UseFormReturn<any>
  isBusiness?: boolean
}) {
  const watchHasParking = form.watch("hasParking") as boolean

  return (
    <>
      <FormField
        control={form.control}
        name="hasParking"
        render={({ field }) => (
          <FormItem className="space-y-[5px]">
            <FormFieldLabel label="駐車場" />
            <label className="flex items-center gap-[6px] py-[5px] cursor-pointer">
              <FormControl>
                <Checkbox
                  checked={field.value as boolean}
                  onCheckedChange={field.onChange}
                  className="size-[18px] rounded border-slate-300 data-[state=checked]:bg-[#354267] data-[state=checked]:border-[#354267]"
                />
              </FormControl>
              <span className="text-sm font-medium text-[#3f4652] tracking-[-0.168px] leading-5">
                駐車場あり
              </span>
            </label>
          </FormItem>
        )}
      />

      {watchHasParking && (
        <>
          <FormField
            control={form.control}
            name="parkingLotNumber"
            render={({ field }) => (
              <FormItem className="space-y-[5px]">
                <FormFieldLabel label="区画番号" />
                <FormControl>
                  <Input
                    placeholder="1"
                    value={(field.value as string) ?? ""}
                    onChange={field.onChange}
                    className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="parkingFee"
            render={({ field }) => (
              <FormItem className="space-y-[5px]">
                <FormFieldLabel label="駐車場代" />
                <FormControl>
                  <div className="flex items-end gap-[6px]">
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="8000"
                      value={(field.value as string) ?? ""}
                      onChange={field.onChange}
                      onKeyDown={handleNumericInput}
                      onPaste={handleNumericPaste}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px] flex-1"
                    />
                    <span className="text-sm font-semibold text-[#3f4652] leading-6">円</span>
                  </div>
                </FormControl>
                <PriceWithTax
                  baseFee={(field.value as string) || "0"}
                  tax={moneyLib.calculateTax(Number(field.value || 0)).toLocaleString()}
                  total={moneyLib.calculateTaxIncluded(Number(field.value || 0))}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </>
  )
}

// その他費用フィールドコンポーネント
export function OtherFeesFields({
  form,
  fields,
  append,
  remove,
  maxCount = 3,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: react-hook-formの型が複雑なためanyを使用
  form: UseFormReturn<any>
  fields: Array<{ id: string } & OtherFee>
  append: (value: OtherFee) => void
  remove: (index: number) => void
  maxCount?: number
}) {
  return (
    <div className="flex flex-col gap-[5px] w-full">
      {fields.map((field, index) => {
        const baseFee = form.watch(`otherFees.${index}.baseFee`) as string | undefined
        const tax = form.watch(`otherFees.${index}.tax`) as string | undefined

        return (
          <div key={field.id} className="flex flex-col gap-2 relative">
            {index === 1 && (
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 my-2.5">
                <span className="block h-[1px] w-full bg-slate-200" />
                <span className="text-sm font-semibold text-slate-300">追加項目</span>
                <span className="block h-[1px] w-full bg-slate-200" />
              </div>
            )}
            <div className="space-y-[5px]">
              <FormFieldLabel label="その他月額費用" />
              <Input
                placeholder="インターネット代"
                {...form.register(`otherFees.${index}.name`)}
                className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex gap-2.5">
                <div className="flex-1 space-y-[5px]">
                  <FormFieldLabel label="基本料金" />
                  <div className="flex items-end gap-[6px]">
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="3000"
                      {...form.register(`otherFees.${index}.baseFee`)}
                      onKeyDown={handleNumericInput}
                      onPaste={handleNumericPaste}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px] flex-1"
                    />
                    <span className="text-sm font-semibold text-[#3f4652] leading-6">円</span>
                  </div>
                </div>

                <div className="flex-1 space-y-[5px]">
                  <FormFieldLabel label="税額" />
                  <div className="flex items-end gap-[6px]">
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="300"
                      {...form.register(`otherFees.${index}.tax`)}
                      onKeyDown={handleNumericInput}
                      onPaste={handleNumericPaste}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px] flex-1"
                    />
                    <span className="text-sm font-semibold text-[#3f4652] leading-6">円</span>
                  </div>
                </div>
              </div>

              <PriceWithTax
                baseFee={baseFee || "0"}
                tax={tax || "0"}
                total={calculateTotal(baseFee, tax)}
              />
            </div>

            {index > 0 && (
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="p-2 rounded-[6px] bg-red-500 font-semibold text-white hover:bg-red-400 flex items-center justify-center gap-2 transition-colors"
                >
                  <Trash className="size-3.5" />
                  削除
                </button>
              </div>
            )}
          </div>
        )
      })}

      {fields.length < maxCount && (
        <Button
          type="button"
          variant="outline"
          onClick={() => append({ name: "", baseFee: "", tax: "" })}
          className="h-10 w-full border-[#9eafc7] text-[#354267] font-bold rounded-[6px] mt-4"
        >
          <Plus className="size-[14px] mr-2" />
          その他費用を追加
        </Button>
      )}
    </div>
  )
}

// 入居者（同居人）の入力欄。賃借人単位で全置換するため、行の追加・削除で完結する。
export function OccupantsFields({
  form,
  fields,
  append,
  remove,
  maxCount = 10,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: react-hook-formの型が複雑なためanyを使用
  form: UseFormReturn<any>
  fields: Array<{ id: string } & Occupant>
  append: (value: Occupant) => void
  remove: (index: number) => void
  maxCount?: number
}) {
  return (
    <div className="flex flex-col gap-[5px] w-full">
      <FormFieldLabel label="入居者" />
      {fields.length === 0 && <p className="text-sm text-slate-400">入居者の登録はありません</p>}

      {fields.map((field, index) => (
        <div key={field.id} className="flex flex-col gap-2 relative">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 my-2.5">
            <span className="block h-[1px] w-full bg-slate-200" />
            <span className="text-sm font-semibold text-slate-300">入居者{index + 1}</span>
            <span className="block h-[1px] w-full bg-slate-200" />
          </div>

          <div className="flex gap-2.5">
            <div className="flex-1 space-y-[5px]">
              <FormFieldLabel label="氏名" required />
              <Input
                placeholder="鈴木一郎"
                {...form.register(`occupants.${index}.name`)}
                className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
              />
              <FormMessage />
            </div>

            <div className="flex-1 space-y-[5px]">
              <FormFieldLabel label="氏名（カナ）" />
              <Input
                placeholder="スズキイチロウ"
                {...form.register(`occupants.${index}.nameKana`)}
                className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
              />
            </div>
          </div>

          <div className="flex gap-2.5">
            <div className="flex-1 space-y-[5px]">
              <FormFieldLabel label="電話番号" />
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={20}
                placeholder="09012345678"
                {...form.register(`occupants.${index}.phone`)}
                onKeyDown={handleNumericInput}
                onPaste={handleNumericPaste}
                className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
              />
            </div>

            <div className="flex-1 space-y-[5px]">
              <FormFieldLabel label="生年月日" />
              <Input
                type="date"
                {...form.register(`occupants.${index}.birthday`)}
                className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
              />
            </div>
          </div>

          <div className="space-y-[5px]">
            <FormFieldLabel label="備考" />
            <Input
              placeholder="備考"
              {...form.register(`occupants.${index}.note`)}
              className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
            />
          </div>

          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={() => remove(index)}
              className="p-2 rounded-[6px] bg-red-500 font-semibold text-white hover:bg-red-400 flex items-center justify-center gap-2 transition-colors"
            >
              <Trash className="size-3.5" />
              削除
            </button>
          </div>
        </div>
      ))}

      {fields.length < maxCount && (
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            append({
              name: "",
              nameKana: "",
              phone: "",
              birthday: "",
              note: "",
            })
          }
          className="h-10 w-full border-[#9eafc7] text-[#354267] font-bold rounded-[6px] mt-4"
        >
          <Plus className="size-[14px] mr-2" />
          入居者を追加
        </Button>
      )}
    </div>
  )
}
